/**
 * CardPreview — live preview of a pipeline card with fake data.
 *
 * Renders a card layout matching the current PipelineCardConfig draft,
 * using fake property/association values to give users a realistic
 * preview of how their card layout will look.
 */

import { Clock } from '@untitledui/icons';

import { cx } from '@/utils/cx';

import type { PipelineCardConfig } from '../utils/cardConfig';
import type { FieldPickerOption } from '../utils/cardConfigOptions';

// ── Fake preview data ────────────────────────────────────────────────

const FAKE_PROPERTY_VALUES: Record<string, string> = {
  currency: '$42,500',
  multi_currency: '$42,500',
  number: '1,250',
  percentage: '68%',
  date: 'Jun 30, 2026',
  datetime: 'Jun 30, 2026',
  select: 'In Progress',
  multi_select: 'Tag A, Tag B',
  text: 'Sample text value',
  markdown: 'Sample content',
  phone_number: '+1 (555) 123-4567',
  email: 'alex@example.com',
  url: 'example.com',
  boolean: 'Yes',
};

const FAKE_ASSOCIATION_LABEL = 'Acme Corp';

// ── CardPreviewSlot ──────────────────────────────────────────────────

function CardPreviewSlot({
  slot,
  options,
  showLabel,
}: {
  slot: { type: string; name: string };
  options: Map<string, FieldPickerOption>;
  /** Whether to render the field label above the value */
  showLabel?: boolean;
}) {
  const opt = options.get(`${slot.type}:${slot.name}`);
  if (!opt || !slot.name) {
    return (
      <span className='text-sm text-[var(--color-text-tertiary)] italic'>
        Empty
      </span>
    );
  }
  const fakeValue =
    slot.type === 'association'
      ? FAKE_ASSOCIATION_LABEL
      : (opt.valueType && FAKE_PROPERTY_VALUES[opt.valueType]) ||
        FAKE_PROPERTY_VALUES.text;

  const valueElement = (
    <span className='block truncate text-sm text-[var(--color-text-secondary)] min-w-0'>
      {fakeValue}
    </span>
  );

  if (!showLabel) {
    return valueElement;
  }

  return (
    <div className='flex flex-col gap-0.5 min-w-0'>
      <span className='text-xs font-medium text-[var(--color-text-tertiary)] truncate flex-shrink-0'>
        {opt.label}
      </span>
      {valueElement}
    </div>
  );
}

// ── CardPreview ──────────────────────────────────────────────────────

export function CardPreview({
  config,
  options,
  entityLabel = 'Deal',
}: {
  config: PipelineCardConfig;
  options: Map<string, FieldPickerOption>;
  /** Singular label for the entity type (e.g. "Deal", "Note"). */
  entityLabel?: string;
}) {
  return (
    <div className='rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] shadow-sm w-full max-w-80 overflow-hidden'>
      {/* Title */}
      <div className='px-3 pt-3'>
        <span className='font-semibold text-sm text-[var(--color-text-primary)] line-clamp-2'>
          Example {entityLabel}
        </span>
      </div>

      {/* Rows — index key is correct: this is a read-only preview with no
          reordering, and content-based keys collide when rows share fields */}
      {config.rows.map((row, rowIndex) => {
        const filledSlots = row.slots.filter(s => s.name);
        if (filledSlots.length === 0) {
          return null;
        }
        const isTwoSlots = filledSlots.length > 1;
        return (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={rowIndex}
            className={cx(
              'px-3 mt-2 min-w-0',
              isTwoSlots && 'flex items-end gap-3'
            )}
          >
            {filledSlots.map(slot => (
              <div
                key={`${slot.type}:${slot.name}`}
                className={cx(isTwoSlots && 'flex-1 min-w-0')}
              >
                <CardPreviewSlot
                  slot={slot}
                  options={options}
                  showLabel={slot.showLabel}
                />
              </div>
            ))}
          </div>
        );
      })}

      {/* Footer */}
      <div className='flex items-center justify-between gap-2 mt-3 pt-2 pb-2 px-3 border-t border-[var(--color-border-secondary)]'>
        <div className='flex items-center gap-1.5 min-w-0'>
          <div className='size-5 rounded-full bg-[var(--color-bg-tertiary)] flex items-center justify-center text-[10px] font-medium text-[var(--color-text-secondary)]'>
            SN
          </div>
          <span className='text-xs text-[var(--color-text-secondary)] truncate'>
            Sarah N.
          </span>
        </div>
        <div className='flex items-center gap-1 text-xs text-[var(--color-text-tertiary)] flex-shrink-0'>
          <Clock className='size-3 flex-shrink-0' aria-hidden />
          <span>2h ago</span>
        </div>
      </div>
    </div>
  );
}
